import React from "react";
import { Spin } from "antd";
import { useTranslation } from "react-i18next";
import { SearchObjectTypes } from "@/services/api/entities/search";
import { AppText } from "@/shared/Components";
import type { SearchResponse } from "@/services/api/entities/search";
import type { DebounceSearchRequest } from "../types";
import { HeaderSearchOverlayControls } from "./Controls";
import styles from "./styles.module.scss";
import { SearchListResults } from "./SearchListResults";

type HeaderSearchOverlayProps = {
  data?: SearchResponse;
  isLoading: boolean;
  isError: boolean;
  isSuccess: boolean;
  textQuery: string;
  selectedDataTypes: SearchObjectTypes[];
  setSelectedDataTypes: (dataTypes: SearchObjectTypes[]) => void;
  debounceSearch: (body: DebounceSearchRequest) => void;
  resetSearchData: () => void;
  setOpen: (value: boolean) => void;
};

export const HeaderSearchOverlay = (props: HeaderSearchOverlayProps) => {
  const { data, isLoading, isError, isSuccess, ...rest } = props;
  const contacts = data?.results.find((item) => item.objectType === SearchObjectTypes.PERSON);
  const notes = data?.results.find((item) => item.objectType === SearchObjectTypes.NOTE);
  const { t } = useTranslation();

  const getContent = () => {
    if (!rest.textQuery.trim()) {
      return (
        <div className={styles.plate}>
          <AppText type="secondary">{t("search.addQuery")}</AppText>
        </div>
      );
    }

    if (!props.selectedDataTypes.length) {
      return (
        <div className={styles.plate}>
          <AppText type="secondary">{t("search.selectType")}</AppText>
        </div>
      );
    }

    if (isLoading) {
      return (
        <div className={styles.plate}>
          <Spin />
        </div>
      );
    }

    if (isError) {
      return (
        <div className={styles.plate}>
          <AppText className={styles.errorText}>{t("search.error")}</AppText>
        </div>
      );
    }

    if (isSuccess && !contacts?.items?.length && !notes?.items?.length) {
      return (
        <div className={styles.plate}>
          <AppText type="secondary">{t("search.empty")}</AppText>
        </div>
      );
    }

    return (
      <div className={styles.content}>
        <SearchListResults
          contacts={contacts?.items.map((item) => ({ ...item, type: "person" }))}
          notes={notes?.items.map((item) => ({ ...item, type: "note" }))}
          textQuery={rest.textQuery.trim()}
          setOpen={props.setOpen}
        />
      </div>
    );
  };

  return (
    <div className={styles.overlay}>
      <HeaderSearchOverlayControls {...rest} />
      {getContent()}
    </div>
  );
};
